// hooks/useRepeatOrder.ts (веб)
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CartItem, OrderInterface } from "../../common/types/types";
import { selectAllSaleItems } from "../../store/slices/saleItemListSlice";
import { setCart } from "../../store/slices/cartSlice";
import { openCartModal } from "../../store/slices/commonModalSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import {
  selectForceStores,
  setForceStores,
} from "../../store/slices/storeListSlice";
import { useDisplayPrice } from "../../common/hooks/useDisplayPrice";

interface UseRepeatOrderResult {
  repeatOrder: (order: OrderInterface) => void;
  isRepeatDisabled: (order: OrderInterface) => boolean;
}

export const useRepeatOrder = (): UseRepeatOrderResult => {
  const dispatch = useDispatch();
  const allItems = useSelector(selectAllSaleItems);
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);
  const forceStore = currentCluster?.forceStore ?? false;
  const getPrice = useDisplayPrice();

  const isRepeatDisabled = useCallback(
    (order: OrderInterface): boolean => {
      if (!order.items || order.items.length === 0) {
        return true;
      }

      if (forceStore) {
        if (!order.storeId) {
          return true;
        }
        if (!selectedStoreIds.includes(order.storeId)) {
          return true;
        }
      }

      const allItemsExist = order.items.every((orderItem) => {
        const itemId = Number(orderItem.itemId);
        const found = allItems.some((item) => Number(item.id) === itemId);
        return found;
      });

      return !allItemsExist;
    },
    [forceStore, selectedStoreIds, allItems],
  );

  const repeatOrder = useCallback(
    (order: OrderInterface) => {
      if (!order.items || order.items.length === 0) return;
      if (isRepeatDisabled(order)) return;

      if (
        forceStore &&
        order.storeId &&
        !selectedStoreIds.includes(order.storeId)
      ) {
        dispatch(setForceStores([order.storeId]));
      }

      const cartItems: CartItem[] = (order.items || [])
        .map((orderItem) => {
          const itemId = Number(orderItem.itemId);
          const saleItem = allItems.find((item) => Number(item.id) === itemId);

          if (!saleItem) return null;

          const currentPrice = getPrice(saleItem.prices);

          const cartItem: CartItem = {
            id: String(itemId),
            name: saleItem.name,
            price: Number(currentPrice),
            quantity: orderItem.quantity,
            image: saleItem.files?.[0],
            minQuantity: saleItem.minQuantity ?? 1,
            quantityStep: saleItem.quantityStep ?? 1,
            attributes: saleItem.attributes,
          };

          if (saleItem.categoryId) {
            cartItem.categoryId = Number(saleItem.categoryId);
          }
          return cartItem;
        })
        .filter((item): item is CartItem => item !== null);

      if (cartItems.length === 0) return;

      dispatch(setCart(cartItems));
      dispatch(openCartModal());
    },
    [
      dispatch,
      allItems,
      forceStore,
      selectedStoreIds,
      isRepeatDisabled,
      getPrice,
    ],
  );

  return { repeatOrder, isRepeatDisabled };
};
